import React from 'react';
import { DvprItem, TestStatus } from '../types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { AlertTriangle, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { useAppContext } from '../contexts/AppContext';

interface DashboardProps {
  dvprItems: DvprItem[];
}

export const Dashboard: React.FC<DashboardProps> = ({ dvprItems }) => {
  const { navigate } = useAppContext();
  const totalTests = dvprItems.length;
  const completed = dvprItems.filter(i => i.status === TestStatus.COMPLETED).length;
  const inProgress = dvprItems.filter(i => i.status === TestStatus.IN_PROGRESS).length;
  const failed = dvprItems.filter(i => i.failures > 0).length;

  const statusData = [
    { name: 'Completed', value: completed },
    { name: 'In Progress', value: inProgress },
    { name: 'Planned', value: totalTests - completed - inProgress },
  ];

  const COLORS = ['#10b981', '#3b82f6', '#94a3b8'];

  // Mock reliability growth data
  const growthData = [
    { stage: 'Proto A', reliability: 65 },
    { stage: 'Proto B', reliability: 78 },
    { stage: 'EVT', reliability: 85 },
    { stage: 'DVT', reliability: 92 },
    { stage: 'PVT', reliability: 98 },
  ];

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Tests"
          value={totalTests.toString()}
          icon={<Clock className="text-blue-500" />}
          trend="+2 this week"
        />
        <StatCard
          title="Completion Rate"
          value={`${Math.round((completed / totalTests) * 100) || 0}%`}
          icon={<CheckCircle className="text-emerald-500" />}
          trend="On track"
        />
        <StatCard
          title="Failures Detected"
          value={failed.toString()}
          icon={<AlertTriangle className="text-red-500" />}
          trend="Requires attention"
          alert
        />
        <StatCard
          title="Confidence Level"
          value="95%"
          icon={<AlertCircle className="text-indigo-500" />}
          trend="R90C95 Target"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Reliability Growth (Demonstrated)</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                <XAxis dataKey="stage" axisLine={false} tickLine={false} />
                <YAxis axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: '#f1f5f9' }} />
                <Bar dataKey="reliability" fill="#6366f1" radius={[4, 4, 0, 0]} barSize={40} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Test Status Distribution</h3>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statusData}
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={5}
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-4 text-sm text-slate-500 mt-2">
            {statusData.map((entry, index) => (
              <div key={entry.name} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: COLORS[index] }}></div>
                {entry.name}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Activity Table Snippet */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
          <h3 className="font-semibold text-slate-800">Critical Issues</h3>
          <button
            onClick={() => navigate('/action-tracking')}
            className="text-sm text-indigo-600 font-medium hover:text-indigo-700"
          >
            View All
          </button>
        </div>
        <div className="divide-y divide-slate-100">
          {dvprItems.filter(i => i.failures > 0).length > 0 ? (
            dvprItems.filter(i => i.failures > 0).map(item => (
              <div key={item.id} className="p-4 flex items-start gap-4 hover:bg-slate-50 transition-colors">
                <div className="mt-1">
                  <AlertTriangle size={16} className="text-red-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">{item.testMethod} - {item.failures} Failure(s)</p>
                  <p className="text-xs text-slate-500 mt-1">{item.comments || "No comments logged."}</p>
                </div>
                <div className="ml-auto">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    High Priority
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="p-8 text-center text-slate-500 text-sm">No active critical issues detected.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon, trend, alert = false }: any) => (
  <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-100">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-sm font-medium text-slate-500">{title}</p>
        <h3 className="text-2xl font-bold text-slate-900 mt-2">{value}</h3>
      </div>
      <div className={`p-2 rounded-lg ${alert ? 'bg-red-50' : 'bg-slate-50'}`}>
        {icon}
      </div>
    </div>
    <div className={`mt-4 text-xs font-medium ${alert ? 'text-red-600' : 'text-emerald-600'}`}>
      {trend}
    </div>
  </div>
);
